import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Paper,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Alert,
  CircularProgress,
  Divider,
  Chip,
  Snackbar,
} from '@mui/material';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import SendIcon from '@mui/icons-material/Send';
import TableChartIcon from '@mui/icons-material/TableChart';
import * as XLSX from 'xlsx';
import { sendMail } from '../services/mailService';

const DEFAULT_SUBJECT = 'Vadesi Geçen Ödemeler Hakkında';
const DEFAULT_CONTENT = `<p>Değerli <b>[[title]]</b>;</p><p><b>[[donem]]</b> ve öncesi döneme ait borç bakiyesi bulunan müşterilerimizin ödeme vadelerinin dolduğunu hatırlatır, ilgili tedarik süreçlerinizde aksaklık yaşanmaması adına <b>[[cariBakiye]]</b> TL'lik bakiyenizin <b>[[sonOdemeTarihi]]</b> tarihi mesai bitimine kadar ödenmesini önemle rica ederiz.</p><br><p>Saygılarımızla,<br>AVM Lojistik Tic. A.Ş.<br>Finans Departmanı</p><br><p style="color:red;"><strong>İletişim Bilgilerimiz:</strong></p><p>Burçin Seis : <a href="mailto:burcin.seis@malllogistics.com">burcin.seis@malllogistics.com</a><br>Tolga Uzunoğlu : <a href="mailto:tolga.uzunoglu@malllogistics.com">tolga.uzunoglu@malllogistics.com</a><br>Demet Uğuz : <a href="mailto:demet.uguz@malllogistics.com">demet.uguz@malllogistics.com</a><br>İrem Gül : <a href="mailto:irem.gul@malllogistics.com">irem.gul@malllogistics.com</a></p><br><p style="color:red;"><strong>Banka Bilgilerimiz:</strong></p><p>Vakıflar Bankası / Dikilitaş Şb<br>TR32 0001 5001 5800 7365 9459 84</p><p>Garanti Bankası / Pendik Ticari Şb<br>TR63 0006 2001 6690 0006 2965 33</p><p>Akbank / Maslak Şb<br>TR47 0004 6006 4588 8000 1587 16</p><p>Ziraat Bankası / Metrokent Şb<br>TR41 0001 0024 5789 6729 8850 01</p><br><hr color="red"><ul><li style="color:red;">Bu mail AVM Lojistik Ticaret A.Ş. sistemi tarafından bilgilendirme amaçlı otomatik olarak gönderilmiştir.</li><li style="color:red;">Bu ileti, takip edilmeyen bir e-posta adresinden gönderilmiştir. Lütfen bu iletiyi yanıtlamayınız.</li><li style="color:red;">Bilgi paylaşımlarınız için <a href="mailto:mutabakat@malllogistics.com">mutabakat@malllogistics.com</a> mail adresini kullanabilirsiniz.</li></ul><hr color="red">`;

export default function SendMailPage() {
  const [subject, setSubject] = useState(DEFAULT_SUBJECT);
  const [content, setContent] = useState(DEFAULT_CONTENT);
  const [recipients, setRecipients] = useState([]);
  const [fileName, setFileName] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPreview, setShowPreview] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const fileInputRef = useRef(null);
  const navigate = useNavigate();

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setFileName(file.name);
    setError('');

    const reader = new FileReader();
    reader.onload = (evt) => {
      try {
        const data = new Uint8Array(evt.target.result);
        const workbook = XLSX.read(data, { type: 'array' });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        const jsonData = XLSX.utils.sheet_to_json(worksheet, { defval: '' });

        if (!jsonData || jsonData.length === 0) {
          setError('Excel dosyasında veri bulunamadı.');
          setRecipients([]);
          return;
        }

        // Sütun isimlerini normalize et
        const normalized = jsonData.map((row) => {
          const keys = Object.keys(row);
          // mail/email sütununu bul
          const mailKey = keys.find(
            (k) => k.toLowerCase().includes('mail') || k.toLowerCase().includes('email') || k.toLowerCase() === 'e-posta'
          );
          const donemKey = keys.find((k) => k.toLowerCase().includes('dönem') || k.toLowerCase().includes('donem'));
          const bakiyeKey = keys.find(
            (k) =>
              k.toLowerCase().includes('bakiye') ||
              k.toLowerCase().includes('cari') ||
              k.toLowerCase().includes('tutar')
          );
          const odemeKey = keys.find(
            (k) =>
              k.toLowerCase().includes('ödeme') ||
              k.toLowerCase().includes('odeme') ||
              k.toLowerCase().includes('vade') ||
              k.toLowerCase().includes('tarih')
          );
          const titleKey = keys.find(
            (k) =>
              k.toLowerCase().includes('firma') ||
              k.toLowerCase().includes('yetkili') ||
              k.toLowerCase().includes('title') ||
              k.toLowerCase().includes('unvan')
          );

          return {
            mail: mailKey ? String(row[mailKey]) : String(row[keys[0]] || ''),
            donem: donemKey ? String(row[donemKey]) : '',
            cariBakiye: bakiyeKey ? String(row[bakiyeKey]) : '',
            sonOdemeTarihi: odemeKey ? String(row[odemeKey]) : '',
            title: titleKey ? String(row[titleKey]) : '',
            _raw: row,
          };
        }).filter((r) => r.mail && r.mail.includes('@'));

        if (normalized.length === 0) {
          setError('Geçerli e-posta adresi bulunamadı. Lütfen "mail" veya "email" sütunu içeren bir Excel dosyası yükleyin.');
          setRecipients([]);
          return;
        }

        setRecipients(normalized);
        setShowPreview(true);
      } catch (err) {
        setError('Dosya okunurken hata oluştu: ' + err.message);
      }
    };
    reader.readAsArrayBuffer(file);
    // Input'u sıfırla (aynı dosyayı tekrar seçebilmek için)
    e.target.value = '';
  };

  const handleSend = async () => {
    if (!recipients.length) return;
    setLoading(true);
    setError('');
    try {
      const mailTransactions = recipients.map((r) => ({
        mail: r.mail,
        donem: r.donem,
        cariBakiye: r.cariBakiye,
        sonOdemeTarihi: r.sonOdemeTarihi,
      }));

      await sendMail({
        mailTransactions,
        subject,
        content,
      });

      setSnackbarOpen(true);
      setTimeout(() => {
        navigate('/sent-mails');
      }, 1800);
    } catch (err) {
      setError('Mail gönderilirken hata oluştu: ' + (err.response?.data?.message || err.message));
    } finally {
      setLoading(false);
    }
  };

  const hasRecipients = recipients.length > 0;

  return (
    <Box>
      <Typography variant="h5" fontWeight={600} sx={{ mb: 3 }}>
        Mail Gönder
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <Paper elevation={2} sx={{ p: 3, borderRadius: 2, mb: 3 }}>
        <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>
          Mail İçeriği
        </Typography>
        <TextField
          label="Konu"
          fullWidth
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
          sx={{ mb: 2 }}
        />
        {/* HTML içerik gizli - POST edilirken kullanılır */}
        <textarea value={content} onChange={(e) => setContent(e.target.value)} style={{ display: 'none' }} readOnly />
        {/* Kullanıcıya render edilmiş HTML önizleme */}
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          Mail Önizleme:
        </Typography>
        <Box
          sx={{
            border: '1px solid #e0e0e0',
            borderRadius: 1,
            p: 2,
            bgcolor: '#fafafa',
            maxHeight: 320,
            overflowY: 'auto',
            fontSize: '0.87rem',
          }}
          dangerouslySetInnerHTML={{ __html: content }}
        />
      </Paper>

      <Paper elevation={2} sx={{ p: 3, borderRadius: 2, mb: 3 }}>
        <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>
          Alıcılar
        </Typography>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
          <input
            type="file"
            accept=".xlsx,.xls,.csv"
            ref={fileInputRef}
            style={{ display: 'none' }}
            onChange={handleFileChange}
          />
          <Button
            variant="outlined"
            startIcon={<UploadFileIcon />}
            onClick={() => fileInputRef.current?.click()}
          >
            Gönderilecek Kişileri Dosyadan Yükle
          </Button>

          {fileName && (
            <Chip label={fileName} onDelete={() => { setFileName(''); setRecipients([]); setShowPreview(false); }} />
          )}

          {hasRecipients && (
            <Button
              variant="outlined"
              startIcon={<TableChartIcon />}
              onClick={() => setShowPreview(!showPreview)}
              color="info"
            >
              DOSYA ÖNİZLEME ({recipients.length} alıcı)
            </Button>
          )}

          {hasRecipients && (
            <Button
              variant="contained"
              startIcon={loading ? <CircularProgress size={18} color="inherit" /> : <SendIcon />}
              onClick={handleSend}
              disabled={loading}
              sx={{ bgcolor: '#1565c0', '&:hover': { bgcolor: '#0d47a1' } }}
            >
              {loading ? 'Gönderiliyor...' : `Dosyadan seçilen ${recipients.length} alıcıya mail gönder`}
            </Button>
          )}
        </Box>

        {!hasRecipients && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            Excel dosyası yükleyerek alıcı listesini oluşturun. Dosyanın <strong>mail</strong> (veya <strong>email</strong>) sütunu içermesi gerekmektedir.
          </Typography>
        )}

        <Typography variant="body2" sx={{ mt: 2 }}>
          Örnek Excel Dosyasını indirmek için{' '}
          <a target="_blank" rel="noreferrer" href="https://muhasebe.malllogistics.com/SampleImportFile.xlsx">
            tıklayınız...
          </a>
        </Typography>
      </Paper>

      {/* Önizleme Tablosu */}
      {hasRecipients && showPreview && (
        <Paper elevation={2} sx={{ borderRadius: 2 }}>
          <Box sx={{ px: 3, pt: 2.5, pb: 1.5 }}>
            <Typography variant="subtitle1" fontWeight={600}>
              Dosya Önizleme
            </Typography>
          </Box>
          <Divider />
          <TableContainer sx={{ maxHeight: 400 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700, bgcolor: '#fafafa' }}>#</TableCell>
                  <TableCell sx={{ fontWeight: 700, bgcolor: '#fafafa' }}>MAIL</TableCell>
                  <TableCell sx={{ fontWeight: 700, bgcolor: '#fafafa' }}>FİRMA / YETKİLİ</TableCell>
                  <TableCell sx={{ fontWeight: 700, bgcolor: '#fafafa' }}>DÖNEM</TableCell>
                  <TableCell sx={{ fontWeight: 700, bgcolor: '#fafafa' }}>CARİ BAKİYE</TableCell>
                  <TableCell sx={{ fontWeight: 700, bgcolor: '#fafafa' }}>SON ÖDEME TARİHİ</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {recipients.map((r, idx) => (
                  <TableRow key={idx} hover>
                    <TableCell>{idx + 1}</TableCell>
                    <TableCell>{r.mail}</TableCell>
                    <TableCell>{r.title || '-'}</TableCell>
                    <TableCell>{r.donem || '-'}</TableCell>
                    <TableCell>{r.cariBakiye || '-'}</TableCell>
                    <TableCell>{r.sonOdemeTarihi || '-'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {/* Başarı Bildirimi */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert severity="success" sx={{ width: '100%' }} onClose={() => setSnackbarOpen(false)}>
          Mailler başarıyla gönderildi! Yönlendiriliyorsunuz...
        </Alert>
      </Snackbar>
    </Box>
  );
}
